import {
  HotspotItem,
  RelativePosition
} from 'content/models/LessonComponentContent';
import React, { MouseEventHandler, useEffect, useState } from 'react';

import AssetService from '../../../shared/services/AssetService';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CancelIcon from '@mui/icons-material/Cancel';
import DefaultImage from '../../images/default-image.jpg';
import { DropResult } from 'react-beautiful-dnd';
import { HotspotComponent } from '../../models/LessonComponent';
import HotspotZoomed from './HotspotZoomed';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import ItemList from './ItemList';
import { ItemModel } from '../../models/ItemModel';
import LessonComponentService from '../../services/LessonComponentService';
import NewComponentService from '../../services/NewComponentService';
import { Typography } from '@mui/material';
import VisitedIcon from '../../images/visited.svg';
import { cloneDeep } from 'lodash';
import styles from '../../styles/Hotspot.module.css';
import { useLessonContext } from '../../models/LessonContext';
import { useLessonEditContext } from '../../models/LessonEditContext';
import useScreenSize from '../../hooks/useScreenSize';

interface HotspotProps {
  component: HotspotComponent;
  onSubmit: (value: string) => void; //* The 'value' is the visited hotspot ids as JSON string.
}
/** Zoom-in scale. */
const ZOOM_SCALE = 2;

/**
 * Calculate the percentage background zoom origin based on the point of
 * interest coordinate. This version zooms the point of interest to the center
 * of the container, but not beyond the edge of the image.
 * @param x Fractional position along axis where to set the focus.
 * @param clipWidth Fractional width of the viewport, if less than the container.
 */
const findOrigin = (x: number, clipWidth: number = 1) =>
  100 *
  Math.max(
    0,
    Math.min(
      1 + (1 - clipWidth),
      x + (x - 0.5) / (ZOOM_SCALE - 1) + (1 - clipWidth) / 2
    )
  );

export default function Hotspot(props: HotspotProps) {
  const url = AssetService.getAssetUrl(props.component.settings.assetkey);

  /** Value indicating whether the image should be zoomed. */
  const [zoomed, setZoomed] = useState(false);
  /** All hotspots in the image */
  const hotSpots = props.component.content.hotspot || [];
  /** Current hotspot index */
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  /** Current hotspot item to zoom */
  const currentItem: HotspotItem =
    currentIndex !== null && hotSpots.length && currentIndex <= hotSpots.length
      ? hotSpots[currentIndex]
      : null;

  const screenWidth = useScreenSize();
  const { handleSettingsChange } = useLessonEditContext();

  const numItems = hotSpots.length || 0;

  /** Hotspots those are viewed and submitted as JSON string by the learner */
  let visited: ItemModel[] =
    (props.component.answer && JSON.parse(props.component.answer)) || [];

  const { mode } = useLessonContext();

  useEffect(() => {
    if (
      currentItem &&
      !visited.find((i) => i.itemId === currentItem.id) &&
      LessonComponentService.canSubmit(mode)
    ) {
      /** Submit viewed hotspot as JSON string */
      props.onSubmit(JSON.stringify([...visited, { itemId: currentItem.id }]));
    }
  }, [currentItem]);

  /** check screenwidth to set the image width */
  const isMobileView = screenWidth <= 500;

  /** Create new hotspot on the click event of the image div */
  const onClick: React.MouseEventHandler<HTMLElement> = (e) => {
    if (mode !== 'edit') return;

    if (!zoomed) {
      const newHotspot = NewComponentService.createHotspot();
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      const xAxis = (e.clientX - rect.left) / rect.width;
      const yAxis = (e.clientY - rect.top) / rect.height;
      const _current = { ...newHotspot, position: { x: xAxis, y: yAxis } };
      const copy = cloneDeep(hotSpots);
      copy.push(_current);
      handleChange(copy);
      setCurrentIndex(copy.length - 1);
      setZoomed(true);
    }
  };

  const handleTextChange = (item: HotspotItem) => {
    const copy = cloneDeep(props.component.content.hotspot);
    copy[currentIndex] = item;
    handleChange(copy);
  };

  const handleChange = (items: HotspotItem[]) => {
    const copy = cloneDeep(props.component);
    copy.content.hotspot = items;
    handleSettingsChange(copy);
  };

  /** Update the position for the given item. */
  const handleUpdatePosition = (
    index: number,
    newPosition: RelativePosition
  ) => {
    if (hotSpots && hotSpots[index]) {
      const copy = cloneDeep(hotSpots);
      copy[index].position = newPosition;
      handleChange(copy);
    }
  };

  /** Returns true if the current hotspot item is visited  */
  const isVisited = visited
    ?.map(({ itemId }) => itemId)
    .includes(currentItem?.id);

  const handlePreviousItem = () => {
    setCurrentIndex((current) => {
      return current === 0 ? numItems - 1 : current - 1;
    });
  };

  const handleNextItem = () => {
    setCurrentIndex((current) => {
      return current === numItems - 1 ? 0 : current + 1;
    });
  };

  /** Set transform origin only when zooming in. */
  const [transformOrigin, setTransformOrigin] = useState<string>('');
  useEffect(() => {
    if (currentItem && zoomed) {
      // When zoomed, the image takes up 63% width, the other 37% width are
      // covered by the hotspot content (in desktop view).
      const originX = findOrigin(
        currentItem.position.x,
        zoomed && !isMobileView ? 0.63 : 1
      );
      const originY = findOrigin(currentItem.position.y);
      setTransformOrigin(`${originX}% ${originY}%`);
    }
  }, [currentItem, zoomed]);

  //#region Drag hotspot.
  /** Event handler triggered when a (zoomed) item drag starts. */
  const [onHotspotDown, setOnHotspotDown] = useState<
    MouseEventHandler<HTMLElement> | undefined
  >();
  /** Page coordinates where the mouse was down during hotspot drag. */
  const [currentPt, setCurrentPt] = useState<RelativePosition | undefined>();
  /** Scale factor between mouse move and fractional hotspot position. */
  const [dragScale, setDragScale] = useState<RelativePosition | undefined>();
  /** Value indicating whether the down..up constituted a rearrangement drag. */
  const [isDrag, setIsDrag] = useState(false);

  useEffect(() => {
    /** Read page coordinates from the mouse event. */
    const eventPt = (e: MouseEvent) => ({ x: e.pageX, y: e.pageY });
    /** Release the event handlers when the drag ends or component unloads. */
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      setCurrentPt(undefined);
    };
    /** Update coordinates during the drag. */
    const move = (e: MouseEvent) => {
      if (e && dragScale && currentPt && currentItem) {
        const container = { offsetWidth: 536, offsetHeight: 301 }; // TODO: Get this from dom reference
        const pt = eventPt(e);
        const pos = cloneDeep(currentItem.position);
        pos.x -= (pt.x - currentPt.x) * dragScale.x;
        pos.y -= (pt.y - currentPt.y) * dragScale.x;
        pos.x = Math.max(0, Math.min(1, pos.x));
        pos.y = Math.max(0, Math.min(1, pos.y));
        handleUpdatePosition(currentIndex, pos);
        setCurrentPt(pt);
        setIsDrag(true);
      }
    };
    if (!onHotspotDown) {
      setOnHotspotDown(() => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        const container = e.currentTarget.parentElement.parentElement;
        setDragScale({
          x: 1 / (ZOOM_SCALE * container.offsetWidth),
          y: 1 / (ZOOM_SCALE * container.offsetHeight)
        });
        setIsDrag(false);
        setCurrentPt(eventPt(e.nativeEvent));
      });
    }
    if (currentPt) {
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    }
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
  });

  //#endregion

  const handleDragEnd = (result: DropResult) => {
    // prevent from destination index outbound error
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const targetIndex = result.destination.index;
    const copy = cloneDeep(hotSpots);
    const item = copy[sourceIndex];
    copy.splice(sourceIndex, 1);
    copy.splice(targetIndex, 0, item);
    handleChange(copy);
  };

  const handleDelete = (id: string) => {
    const copy = cloneDeep(hotSpots);
    const index = copy.findIndex((i) => i.id === id);
    if (index >= 0) {
      copy.splice(index, 1);
      handleChange(copy);
    }
  };

  return (
    <>
      <div
        className={`${styles.viewFrame} ${mode === 'edit' ? styles.edit : ''} `}
      >
        <div
          className={styles.imageContainer}
          style={{
            transform: `scale(${zoomed ? ZOOM_SCALE : 1})`,
            transformOrigin
          }}
          onClick={onClick}
        >
          <img
            className={styles.image}
            src={url || DefaultImage}
            alt="Sample hotspot image"
          />
          {hotSpots
            .filter((i) => !zoomed || i.id === currentItem.id)
            .map((hotspot, idx) => (
              <Box
                key={hotspot.id}
                className={styles.marker}
                style={{
                  left: `${100 * hotspot.position.x}%`,
                  top: `${100 * hotspot.position.y}%`
                }}
              >
                <div
                  className={zoomed ? styles.zoomed : ''}
                  onMouseDown={
                    zoomed && mode === 'edit' ? onHotspotDown : undefined
                  }
                >
                  <IconButton
                    className={styles.infoIcon}
                    onClick={(e) => {
                      if (!zoomed || !isDrag) {
                        e.stopPropagation();
                        setCurrentIndex(idx);
                        setZoomed(!zoomed);
                      }
                    }}
                    color="primary"
                  >
                    <InfoIcon style={{ fontSize: zoomed ? '14px' : '28px' }} />
                  </IconButton>
                </div>
                {mode === 'edit' && !zoomed && (
                  <>
                    <div
                      className={`${
                        hotspot.position.x > 0.9 ? styles.positionLeft : ''
                      } ${
                        hotspot.position.y < 0.12 ? styles.positionBottom : ''
                      }`}
                    >
                      <IconButton
                        className={styles.cancelIcon}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(hotspot.id);
                        }}
                      >
                        <CancelIcon />
                      </IconButton>
                    </div>
                    <div
                      className={`${
                        hotspot.position.x > 0.9
                          ? styles.avatarPositionLeft
                          : ''
                      } ${
                        hotspot.position.y < 0.12
                          ? styles.avatarPositionBottom
                          : ''
                      }`}
                    >
                      <Avatar className={styles.avatarIcon}>
                        <Typography variant="caption">{idx + 1}</Typography>
                      </Avatar>
                    </div>
                  </>
                )}
              </Box>
            ))}
        </div>
        {zoomed && currentItem && (
          /** Once the hotspot is clicked/zoomed the text editor/display will be shown */
          <>
            <HotspotZoomed
              componentkey={props.component.componentkey}
              currentItem={currentItem}
              onChange={handleTextChange}
              onZoomOut={() => {
                setCurrentIndex(null);
                setZoomed(false);
              }}
              onPreviousItem={handlePreviousItem}
              onNextItem={handleNextItem}
            />
            {mode === 'mark' && isVisited && (
              <div className={styles.visitedPosition}>
                <img src={VisitedIcon} alt="visited" />
              </div>
            )}
          </>
        )}
      </div>
      {mode === 'edit' && !zoomed && (
        <ItemList
          items={hotSpots}
          onDragEnd={handleDragEnd}
          onDelete={handleDelete}
        />
      )}
    </>
  );
}
